import { logError, logEvent } from "@src/appV2/lib/analytics";
import {
  MutationCache,
  QueryCache,
  QueryClient,
  type QueryObserverOptions,
} from "@tanstack/react-query";
import { enqueueSnackbar } from "notistack";
import { useEffect, useRef } from "react";

import { isDevelopmentNodeEnvironment } from "../environment";
import { isCbhApiAxiosError } from "../Errors/errorUtils";
import { CbhFeatureFlag, useCbhFlag } from "../FeatureFlags";

export const defaultQueryOptions: QueryObserverOptions = {
  refetchOnWindowFocus: true,
  retry: 2,
  retryDelay: 1000,
  useErrorBoundary: isDevelopmentNodeEnvironment(),
};

let cbhQueryClient: QueryClient | undefined;

function getErrorMessage(
  error: unknown,
  errorMessages: Record<string, string>,
  defaultMessage: string
) {
  if (isCbhApiAxiosError(error)) {
    const errorResponse = error.response.data.errors.find((error) => error.code);

    if (errorResponse && errorMessages[errorResponse.code]) {
      return errorMessages[errorResponse.code];
    }
  }

  return defaultMessage;
}

// Doesn't work on v4, https://tanstack.com/query/latest/docs/framework/react/typescript#typing-meta
interface Meta {
  logSuccessMessage?: string;
  userSuccessMessage?: string;
  logErrorMessage?: string;
  userErrorMessage?: string;
  toastAutoHideDurationInMilliseconds?: number;
}

// eslint-disable-next-line sonarjs/cognitive-complexity
export function useCbhQueryClient() {
  const errorMessages = useCbhFlag(CbhFeatureFlag.HCP_APP_ERROR_MESSAGES, { defaultValue: {} });
  const errorMessagesRef = useRef(errorMessages);

  // eslint-disable-next-line logical-assignment-operators
  if (!cbhQueryClient) {
    cbhQueryClient = new QueryClient({
      queryCache: new QueryCache({
        onError: (error, query) => {
          const { logErrorMessage, userErrorMessage } = (query.meta ?? {}) as Meta;

          if (logErrorMessage) {
            logError(String(logErrorMessage), { error });
          }

          if (userErrorMessage) {
            enqueueSnackbar({
              variant: "error",
              message: userErrorMessage,
            });
          }
        },
        onSuccess: (_, query) => {
          const { logSuccessMessage, userSuccessMessage } = (query.meta ?? {}) as Meta;

          if (logSuccessMessage) {
            logEvent(String(logSuccessMessage));
          }

          if (userSuccessMessage) {
            enqueueSnackbar({
              variant: "success",
              message: userSuccessMessage,
            });
          }
        },
      }),
      mutationCache: new MutationCache({
        onError(error, _variables, _context, mutation) {
          const { logErrorMessage, userErrorMessage } = (mutation.meta ?? {}) as Meta;

          if (logErrorMessage) {
            logError(String(logErrorMessage), { error });
          }

          if (userErrorMessage) {
            enqueueSnackbar({
              variant: "error",
              message: getErrorMessage(error, errorMessagesRef.current, userErrorMessage),
            });
          }
        },
        onSuccess(_data, _variables, _context, mutation) {
          const { logSuccessMessage, userSuccessMessage } = (mutation.meta ?? {}) as Meta;

          if (logSuccessMessage) {
            logEvent(String(logSuccessMessage));
          }

          if (userSuccessMessage) {
            enqueueSnackbar({
              variant: "success",
              message: userSuccessMessage,
            });
          }
        },
      }),
      defaultOptions: {
        queries: defaultQueryOptions,
      },
    });
  }

  useEffect(() => {
    errorMessagesRef.current = errorMessages;
  }, [errorMessages]);

  return cbhQueryClient;
}
